// rc-upload fires beforeUpload for every dropped file at once; unbounded POSTs pinned the API. OCR runs 3 at a time.
const UPLOAD_CONCURRENCY = 3;
let inFlightUploads = 0;
const uploadWaiters: Array<() => void> = [];

export const acquireUploadSlot = (): Promise<void> =>
  new Promise((resolve) => {
    const start = () => {
      inFlightUploads += 1;
      resolve();
    };
    if (inFlightUploads < UPLOAD_CONCURRENCY) {
      start();
    } else {
      uploadWaiters.push(start);
    }
  });

export const releaseUploadSlot = (): void => {
  inFlightUploads = Math.max(0, inFlightUploads - 1);
  const next = uploadWaiters.shift();
  if (next) next();
};

// Queued files live only in browser memory and a refresh loses them, so the unload guard asks first.
export const hasPendingUploads = (): boolean => inFlightUploads > 0 || uploadWaiters.length > 0;

// One split at a time: rc-upload fires `beforeUpload` for every dropped file at once.
let splitChain: Promise<void> = Promise.resolve();
let pendingSplits = 0;
export const runSplitInTurn = (task: () => Promise<void>): Promise<void> => {
  pendingSplits += 1;
  const run = splitChain.then(task).finally(() => {
    pendingSplits -= 1;
  });
  splitChain = run.catch(() => undefined);
  return run;
};

// Module-level like the chain: splits outlive the page component that queued them.
export const hasPendingSplits = (): boolean => pendingSplits > 0;

// No response (network, timeout), 5xx, 408 and 429 may pass on a second try; other 4xx will not.
export const isRetryableUploadError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === undefined || status >= 500 || status === 408 || status === 429;
};

export const hasRetryableRows = (rows: Array<{ _localStatus: string; _file?: File }>): boolean =>
  rows.some((row) => row._localStatus === 'upload_failed' && !!row._file);

type UnloadState = {
  authorized: boolean;
  reloading: boolean;
  pendingUploads: boolean;
  pendingSplits: boolean;
  retryableRows: boolean;
};

export const shouldWarnOnUnload = ({
  authorized,
  reloading,
  pendingUploads,
  pendingSplits,
  retryableRows,
}: UnloadState): boolean => authorized && !reloading && (pendingUploads || pendingSplits || retryableRows);

// Only failed rows are lost on a leave: queued work keeps running. A reload of the page (role switch) cannot be undone.
export const shouldConfirmLeave = ({
  authorized,
  retryableRows,
  reloadsPage,
}: Pick<UnloadState, 'authorized' | 'retryableRows'> & { reloadsPage: boolean }) =>
  authorized && retryableRows && !reloadsPage;

// Requests read the token when sent: after a sign-out and another sign-in, a queued file would go up as someone else.
export const mayUploadAs = ({
  authorized,
  ownerId,
  userId,
}: {
  authorized: boolean;
  ownerId: unknown;
  userId: unknown;
}) => authorized && ownerId === userId;

// Toasts about work that outlived the page: the operator is elsewhere and may miss a short one.
export const LATE_TOAST_SECONDS = 10;

export const isPdfName = (name: string): boolean => name.toLowerCase().endsWith('.pdf');

// Ids for client-only rows that have no DB id yet; unique within the tab is enough.
let localIdSeq = 0;
export const nextLocalId = (): string => {
  localIdSeq += 1;
  return `local-${Date.now().toString(36)}-${localIdSeq}`;
};
